import './AppChat.scss';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Chat from '../generics/Chat';
import IconButton from '../common/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { ChatMessage } from '../../models/LLM';
import { useToasts } from '../../contexts/toasts';
import { useLocation } from 'react-router-dom';
import { Button as AntButton, Flex, Select } from 'antd';
import { RedisVariables } from '../../api/RedisVariables';
import { Agents } from '../../api/Agents';
import { EnvironmentVariables } from '../../api/EnvironmentVariables';
import { EnvVariable } from '../../models/Environment';
import { Agent, PersistedChatState, TextPart } from '../../models/Agent';
import Cookies from 'js-cookie';
import { calcResponseTime, isValidAgent, readPersistedState } from '../../utils';
import { EventEmitter } from '../../utils/EventEmitter';
import { Alert, Modal, Button } from 'react-bootstrap';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';

type Props = {
  show: boolean;
  onClose: VoidFunction;
}

type MessageInfo = Pick<ChatMessage, "kind" | "role">;

const filterOption = (input: string, option: any): boolean => option?.value?.toLowerCase().includes(input.toLowerCase());

export default function AppChat({ show, onClose }: Props) {
  // read once on mount, cleared when the state has been restored
  const [storedChatState, setStoredChatState] = useState(readPersistedState);
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error] = useState("");
  const [chatTitle, setChatTitle] = useState(storedChatState?.chatTitle || "");
  const [selectedAgent, setSelectedAgent] = useState<Agent & { key: string }>();
  const [agents, setAgents] = useState<(Agent & { key: string })[]>();
  const [expanded, setExpanded] = useState(!!storedChatState?.expanded);
  const [showSessionModal, setShowSessionModal] = useState(false);

  const eventSourceRef = useRef<EventSource>();
  const abortControllerRef = useRef<AbortController>();
  const interruptionNotifiedRef = useRef(false);
  const sessionIdRef = useRef<string | undefined>();
  // ref to store information that is not always present in events
  // (user messages or reasoning are not fully tracked)
  const messagesRef = useRef<Record<string, MessageInfo>>({});
  // ----------------------------------
  const selectedAgentRef = useRef<Agent & { key: string }>();

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();
  const { pathname } = useLocation();

  const chatSessionPrefix = useMemo(() => window.__env.AI_CHAT__SESSION_PREFIX || "alida-chat-session", []);
  const agentsOptions = useMemo(() => agents?.map(agent => ({ value: agent.key, label: agent.name, contextPaths: agent.contextPaths })), [agents]);
  const selectedAgentName = useMemo(() => selectedAgent?.name, [selectedAgent]);
  const historyIsEmpty = useMemo(() => history.filter(m => !m.isDivider).length === 0, [history]);
  const newSessionDisabled = useMemo(() => loading || historyIsEmpty, [loading, historyIsEmpty]);
  const chatStorageKey = useMemo(() => `${chatSessionPrefix}_state`, [chatSessionPrefix]);

  const filteredAgents = useMemo(() => {
    return agentsOptions?.filter(option => {
      if (!option?.contextPaths || option.contextPaths.length === 0) return true;
      return option.contextPaths.some(page => pathname.includes(page));
    });
  }, [agentsOptions, pathname]);

  useEffect(() => {
    selectedAgentRef.current = selectedAgent;
  }, [selectedAgent]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
    }
  }, []);

  const initAgents = useCallback(() => {
    const [namespace, key] = window.__env.AGENTS_REDIS_VARIABLE?.split(':') || ['system', 'agents'];

    RedisVariables.getRedisVariable(key, namespace)
      .then(
        ({ data }) => {
          const agentsList: Agent[] = JSON.parse(data?.value);
          if (!agentsList || !Array.isArray(agentsList)) return;
          const mappedAgents = agentsList.map((a, index) => ({ ...a, key: btoa(`${a.url}-redis-${index}`) }));
          setAgents(curr => (curr || []).concat(mappedAgents));
          const defaultAgent = mappedAgents.find(a => a.default);
          // the agent restored after a refresh takes precedence over the default one
          if (defaultAgent && !readPersistedState()?.agentKey) setSelectedAgent(defaultAgent);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('chat-agents-redis-error', message, `Redis Agents Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );

    EnvironmentVariables.getEnvironmentVariables()
      .then(
        ({ data }) => {
          const agentVariables = (data as EnvVariable[]).filter(v => v.type === "json" && isValidAgent(v.value));
          setAgents(curr => (curr || []).concat(agentVariables.map(v => JSON.parse(v.value as string)).map((a, index) => ({ ...a, key: btoa(`${a.url}-env-${index}`) }))));
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('chat-agents-environment-error', message, `Environment Agents Error ${status ? `(${status})` : ''}`, 'danger');
        },
      );
  }, [showToast]);

  useEffect(initAgents, [initAgents]);

  useEffect(() => {
    EventEmitter.subscribe('chat:agent-added', () => {
      setAgents([]);
      initAgents();
    });
    return () => EventEmitter.unsubscribe('chat:agent-added');
  }, [initAgents]);

  const upsertPart = useCallback((id: string, patch: Partial<ChatMessage> & { text: any }) => {
    setHistory(h => {
      const found = h.findIndex(m => m.timestamp === id); // perchè usa il timestamp?
      // an aborted generation emits parts with no text: they would render as empty clouds
      if (found === -1) return patch.text ? h.concat({ timestamp: id, role: "assistant", agentName: selectedAgentRef.current?.name, ...patch }) : h;
      const newHistory = [...h];
      const { text, ...rest } = patch;
      newHistory[found] = { ...newHistory[found], ...rest, text: text || newHistory[found].text };
      return newHistory;
    });
  }, []);

  const appendPart = useCallback((id: string, delta: string, patch: Partial<ChatMessage> = {}) => {
    setHistory(h => {
      const found = h.findIndex(m => m.timestamp === id);
      if (found === -1) return h.concat({ timestamp: id, role: "assistant", agentName: selectedAgentRef.current?.name, text: delta, ...patch });
      const newHistory = [...h];
      newHistory[found] = { ...newHistory[found], ...patch, text: (newHistory[found].text || "") + delta };
      return newHistory;
    });
  }, []);

  const notifyInterruption = useCallback(() => {
    setLoading(false);
    if (interruptionNotifiedRef.current) return;
    interruptionNotifiedRef.current = true;
    setHistory(h => h.concat({
      timestamp: new Date().toISOString(),
      text: "🛑 Tool interrupted",
      role: "assistant",
      kind: "system",
      agentName: selectedAgentRef.current?.name,
    }));
  }, []);

  const onOpenCodeEvent = useCallback((data: any) => {
    if (!data) return;
    let event;
    try {
      event = JSON.parse(data);
    } catch (err) {
      return;
    }

    const sessionId = sessionIdRef.current;
    const type = event.payload?.type;
    const props = event.payload?.properties;

    if (!props || props.sessionID !== sessionId) return;

    if (type === "session.idle") {
      setLoading(false);
      return;
    }

    if (type === "session.updated") {
      const title = props?.info?.title;
      // TODO save title in session cookie
      setChatTitle(title);
      return;
    }

    if (type === "session.error" || type === "error") {
      const err = event.error || props?.error;
      if (err?.name === "MessageAbortedError") {
        notifyInterruption();
        return;
      }
      const text = typeof err === "string" ? err : (err?.data?.message || err?.message || JSON.stringify(err, null, 2) || "Unknown error");
      upsertPart(event.id || `error-${event.timestamp || sessionId}`, { text, isAlert: true, role: "assistant" });
      setLoading(false);
      return;
    }

    if (type === "message.updated" || type === "message.created") {
      const info = props?.info;
      messagesRef.current[info.id] = { role: info.role };
      if (!info || info.role === "user") return;
      // no need to check for user messages
      // The assistant message carries a completion time once it is done
      if (info.time?.completed) setLoading(false);
      const time = calcResponseTime(info.time);
      if (time) {
        let text = `✅ Thought for ${time}s`;
        const tokens = info.tokens?.total;
        if (tokens) text += ` - ${tokens} tokens`;
        upsertPart(info.id, { time: info.time, tokens, text, kind: "system" });
      }
      return;
    }

    if (type === "message.part.delta") {
      const deltaPart = props?.part || props;
      const partId = deltaPart?.id || props?.partID;
      if (!partId) return;
      const messageID = deltaPart?.messageID || props?.messageID;
      if (messagesRef.current[messageID]?.role === "user") return;
      const delta = props?.delta ?? deltaPart?.text ?? props?.text;
      if (!delta) return;
      // this breaks the reasoning rendering when delta parts arrive
      // const kind = deltaPart?.type || props?.field;
      // messagesRef.current[partId] = { ...(messagesRef.current[partId] || {}), kind };
      appendPart(partId, delta, {});
      return;
    }

    if (type !== "message.part.updated") return;
    const part = props?.part;
    if (!part) return;
    if (messagesRef.current[part.messageID]?.role === "user") return;
    messagesRef.current[part.id] = { ...(messagesRef.current[part.id] || {}), kind: part.type };

    switch (part.type) {
      case "text":
        if (part.synthetic || !part.text) return;
        upsertPart(part.id, { text: part.text, role: "assistant" });
        break;

      case "reasoning":
        if (part.text === undefined) return;
        upsertPart(part.id, { text: part.text, role: "assistant", kind: "reasoning", time: part.time });
        break;

      case "tool":
        const s = part.state || {};
        const text = [
          `**🔧 ${part.tool || "tool"}**${s.status ? ` — _${s.status}_` : ""}\n`,
          s.input ? "_Tool input_\n```json\n" + JSON.stringify(s.input, null, 2) + "\n```" : "",
          s.output ? "_Tool result_\n```\n" + (typeof s.output === "string" ? s.output : JSON.stringify(s.output, null, 2)) + "\n```" : "",
        ].filter(Boolean).join("\n");
        upsertPart(part.id, { text, role: "assistant", kind: "tool", time: s.time });
        break;
      default: break;
    }
  }, [appendPart, upsertPart, notifyInterruption]);

  const listenOpenCode = useCallback((agent: Agent) => {
    if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = undefined; }
    messagesRef.current = {};
    if (!agent.streamingUrl) {
      console.warn("Agent 'streamingUrl' not defined, could not open an SSE connection");
      return;
    }
    const params = new URLSearchParams(agent.extra?.queryParams);
    eventSourceRef.current = Agents.listenOpenCode(agent.streamingUrl,
      (evt) => onOpenCodeEvent(evt.data),
      (evt) => {
        console.error("An error occurred during SSE connection", evt);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = undefined;
        }
      },
      params
    );
  }, [onOpenCodeEvent]);

  const createSession = useCallback(async (agent: Agent & { key: string }) => {
    try {
      const { data } = await Agents.createOpenCodeSession(agent.url);
      Cookies.set(`${chatSessionPrefix}_${agent.key}`, data.id);
      sessionIdRef.current = data.id;
      Agents.putOpenCodeSession(data.id)
        .then(
          ({ data: d }) => console.log(d),
          (err) => {
            console.warn("Could not update session", err);
            // const message = err.response?.data?.message || err.message;
            // const status = err.response?.status;
            // showToast('chat-session-error', message, `Session Error ${status ? `(${status})` : ''}`, 'danger');
          }
        );
      return data.id;
    }
    catch (err: any) {
      const errMessage = err.response?.data?.message || err.message;
      const status = err.response?.status;
      setHistory(h => h.concat({ text: errMessage, isAlert: true, timestamp: new Date().toISOString() }));
      showToast('chat-invoke-error', errMessage, `Session Error ${status ? `(${status})` : ''}`, 'danger');
    }
  }, [chatSessionPrefix, showToast]);

  const handleSendMessage = useCallback(async (message: ChatMessage) => {
    setLoading(true);
    interruptionNotifiedRef.current = false;
    setHistory(h => h.concat(message));

    const onError = (err: any) => {
      abortControllerRef.current = undefined;
      // the request was interrupted on purpose, handleMessageStop already notified the user
      if (err.code === "ERR_CANCELED") return;
      setLoading(false);
      const errMessage = err.response?.data?.message || err.message;
      const status = err.response?.status;
      setHistory(h => h.concat({ text: errMessage, isAlert: true, timestamp: new Date().toISOString() }));
      showToast('chat-invoke-error', errMessage, `Invoke Error ${status ? `(${status})` : ''}`, 'danger');
    };

    if (selectedAgent) {
      const protocol = selectedAgent.protocol || "OpenAI";

      if (protocol === "OpenAI") {
        const updatedHistory = history.concat(message).filter((m) => !m.isDivider && m.agentName === selectedAgent.name)
        const body = {
          messages: updatedHistory.map(m => ({
            ...m,
            content: m.text,
          })),
        };
        const controller = new AbortController();
        abortControllerRef.current = controller;
        Agents.sendMessage(selectedAgent.url, body, controller.signal)
          .then(
            ({ data }) => {
              abortControllerRef.current = undefined;
              setLoading(false);
              setHistory(h => h.concat({ timestamp: new Date().toISOString(), text: data.choices?.[0]?.message.content, role: "assistant", agentName: selectedAgent.name }));
            },
            (err) => onError(err),
          )
      } else {
        if (!eventSourceRef.current) listenOpenCode(selectedAgent);

        const chatSession = Cookies.get(`${chatSessionPrefix}_${selectedAgent.key}`) || await createSession(selectedAgent);
        sessionIdRef.current = chatSession;
        const body = { parts: [{ type: "text", text: message.text }] };
        Agents.sendOpenCodeMessage(selectedAgent.url, chatSession!, body)
          .catch(err => onError(err));
      }
    }
  }, [showToast, history, listenOpenCode, chatSessionPrefix, selectedAgent, createSession]);

  const loadSessionMessages = useCallback((agent: Agent & { key: string }, chatSession: string, wasLoading?: boolean) => {
    Agents.getSessionMessages(agent.url, chatSession)
      .then(
        ({ data }) => {
          const messages: ChatMessage[] = data.flatMap(m =>
            m.parts
              .filter((p): p is TextPart => p.type === "text")
              .filter((p) => p.text && !p.synthetic)
              .map((p) => ({
                timestamp: p.id,
                text: p.text,
                isUserPrompt: m.info.role === "user",
                role: m.info.role,
                agentName: agent.name,
              }))
          );
          setHistory(h => h.concat(messages));
          const last = data[data.length - 1]?.info;
          const pending = last && (last.role === "user" ? !!wasLoading : !last.time.completed && !last.error);
          if (pending) setLoading(true);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          if (status === 404) {
            Cookies.remove(`${chatSessionPrefix}_${agent.key}`);
            Agents.deleteOpenCodeSession(chatSession)
              .then(
                ({ data }) => console.log(data),
                (err) => {
                  console.warn("Could not delete session", err);
                  // const message = err.response?.data?.message || err.message;
                  // const status = err.response?.status;
                  // showToast('session-delete-error', message, `Session Delete Error ${status ? `(${status})` : ''}`, 'danger');
                }
              )
            createSession(agent);
          }
          else showToast('chat-session-messages-error', message, `Session Messages Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [chatSessionPrefix, createSession, showToast]);

  const handleAgentChange = useCallback((value: string) => {
    const agent = agents?.find(a => a.key === value);
    if (!agent || agent.key === selectedAgent?.key) return;
    setSelectedAgent(agent);

    setHistory(h => h.concat({
      isDivider: true,
      text: `Now chatting with "${agent.name}"`,
      timestamp: new Date().toISOString(),
    }));

    setStoredChatState(undefined);

    if (agent.protocol === "OpenCode") {
      listenOpenCode(agent);
      const chatSession = Cookies.get(`${chatSessionPrefix}_${agent.key}`);
      sessionIdRef.current = chatSession;
      if (!chatSession) createSession(agent);
      else if (historyIsEmpty) loadSessionMessages(agent, chatSession);
    }
  }, [agents, historyIsEmpty, listenOpenCode, chatSessionPrefix, createSession, selectedAgent, loadSessionMessages]);

  useEffect(() => {
    if (!storedChatState?.agentKey || !agents) return;
    const agent = agents.find(a => a.key === storedChatState.agentKey);
    if (!agent) return;
    setStoredChatState(undefined);
    setSelectedAgent(agent);

    if ((agent.protocol || "OpenAI") === "OpenAI") {
      setHistory(storedChatState.history || []);
      if (storedChatState.loading) notifyInterruption();
      return;
    }

    listenOpenCode(agent);
    const chatSession = Cookies.get(`${chatSessionPrefix}_${agent.key}`);
    sessionIdRef.current = chatSession;
    if (chatSession) loadSessionMessages(agent, chatSession, storedChatState.loading);
  }, [agents, storedChatState, listenOpenCode, chatSessionPrefix, loadSessionMessages, notifyInterruption]);

  useEffect(() => {
    if (storedChatState?.agentKey && !selectedAgent) return;
    const state: PersistedChatState = {
      agentKey: selectedAgent?.key,
      expanded,
      chatTitle,
      loading,
      history: (selectedAgent?.protocol || "OpenAI") === "OpenAI" ? history : undefined,
    };
    try {
      sessionStorage.setItem(chatStorageKey, JSON.stringify(state));
    } catch (err) {
      console.warn("Could not persist chat state", err);
    }
  }, [storedChatState, selectedAgent, expanded, chatTitle, loading, history, chatStorageKey]);

  const handleAgentClear = useCallback(() => {
    if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = undefined; }
    messagesRef.current = {};
    setSelectedAgent(undefined);
  }, []);

  const toggleExpanded = useCallback(() => setExpanded(v => !v), []);

  const handleClose = useCallback(() => {
    setExpanded(false);
    onClose();
  }, [onClose]);

  const handleMessageStop = useCallback(() => {
    if (!selectedAgent) return;

    if ((selectedAgent.protocol || "OpenAI") === "OpenAI") {
      abortControllerRef.current?.abort();
      abortControllerRef.current = undefined;
      notifyInterruption();
      return;
    }

    const sessionId = sessionIdRef.current;
    if (!sessionId) return;

    Agents.abortOpenCodeSession(selectedAgent.url, sessionId)
      .then(
        ({ data }) => { if (data !== false) notifyInterruption(); },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('chat-abort-error', message, `Abort Error ${status ? `(${status})` : ''}`, 'danger');
        },
      );
  }, [selectedAgent, notifyInterruption, showToast]);

  const openSessionModal = useCallback(() => setShowSessionModal(true), []);
  const handleSessionModalClose = useCallback(() => setShowSessionModal(false), []);

  const handleCreateSession = useCallback(async () => {
    if (!selectedAgent) return;

    setHistory([]);
    setChatTitle("");
    messagesRef.current = {};
    interruptionNotifiedRef.current = false;

    if (selectedAgent.protocol === "OpenAI") {
      abortControllerRef.current?.abort();
      abortControllerRef.current = undefined;
    } else {
      Cookies.remove(`${chatSessionPrefix}_${selectedAgent.key}`);
      sessionIdRef.current = undefined;
      if (!eventSourceRef.current) listenOpenCode(selectedAgent);
      showLoader();
      await createSession(selectedAgent);
      hideLoader();
    }

    setShowSessionModal(false);
  }, [selectedAgent, listenOpenCode, createSession, chatSessionPrefix, showLoader, hideLoader]);

  return (
    <nav className={classNames("app-chat", { open: show })}>
      <Chat
        history={history}
        loading={loading}
        onMessageSend={handleSendMessage}
        selectedAgentName={selectedAgentName}
        disabledInput={!selectedAgent}
        className={classNames("app-chat-container", { expanded })}
        onMessageStop={handleMessageStop}
        emptyChatMessage="Hi, how can I help you?"
        error={error}
        extra={
          <div className="chat-header">
            <Flex align="center" justify="space-between">
              <Flex align="center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="title-icon">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                </svg>
                <h6 className="ms-1 mb-0">{chatTitle || "AI Chat"}</h6>
              </Flex>

              <Flex gap={8}>
                <IconButton tooltip={{ mouseEnterDelay: 0.5, title: expanded ? "Reduce" : "Expand" }} className="p-1" onClick={toggleExpanded}>
                  <FontAwesomeIcon icon={expanded ? solid('down-left-and-up-right-to-center') : solid('up-right-and-down-left-from-center')} />
                </IconButton>
                <IconButton tooltip={{ mouseEnterDelay: 0.5, title: "Close" }} className="p-1" onClick={handleClose}>
                  <FontAwesomeIcon icon={solid('times')} />
                </IconButton>
              </Flex>
            </Flex>

            {agentsOptions && (
              <Flex justify="space-between" align="center" className="mt-1">
                <AntButton disabled={newSessionDisabled} onClick={openSessionModal} type="text" size="small">
                  <FontAwesomeIcon icon={solid("plus")} />
                  <span>New session</span>
                </AntButton>

                <Select
                  showSearch
                  filterOption={filterOption}
                  className="w-50"
                  placeholder="Select an agent..."
                  value={selectedAgentName}
                  onChange={handleAgentChange}
                  size="small"
                  onClear={handleAgentClear}
                  options={filteredAgents}
                  allowClear
                />
              </Flex>
            )}
          </div>
        }
      />

      <Modal show={showSessionModal} onHide={handleSessionModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>New Session</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to create new session?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation will delete all messages in the current session and cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleSessionModalClose}>Close</Button>
          <Button variant="success" size="sm" onClick={handleCreateSession}>Confirm</Button>
        </Modal.Footer>
      </Modal>
    </nav>
  )
}
